import { Injectable } from '@angular/core';
import pdfMake, { fonts } from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { formatCNPJ, formatCPF } from "../utils/format.utils";
import { style } from '@angular/animations';

@Injectable({ providedIn: 'root' })
export class SalesOrderReportService {

    constructor() {
        pdfMake.vfs = (pdfFonts as any).vfs;
    }

    generatePDF(order: any) {
        const formatBRL = (value: number) =>
            value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

        const docDefinition: any = {
            content: [
                {
                    table: {
                        headerRows: 1,
                        widths: ['*', 120],
                        body: [
                            [{ text: 'Pedido de compra', bold: true, style: 'tableHeader' }, { text: `Pedido Nº: ${order.order.number}`, style: 'tableHeader' }],
                            [
                                {
                                    table: {
                                        headerRows: 1,
                                        widths: [50, '*'],
                                        body: [
                                            [{ text: 'Dados da empresa', style: 'sectionHeader', colSpan: 2 }, null],
                                            [{ text: 'Empresa:', bold: true }, { text: order.company.name }],
                                            [{ text: 'CNPJ:', bold: true }, { text: formatCNPJ(order.company.cnpj) }],
                                            [{ text: 'Endereço:', bold: true }, { text: order.company.address }],
                                        ]
                                    },
                                    colSpan: 2,
                                    layout: {
                                        hLineWidth: function (i: any, node: { table: { body: string | any[]; }; }) {
                                            return (i === node.table.body.length) ? 1 : 0;
                                        },
                                        vLineWidth: function () { return 0; },
                                        hLineColor: function () { return '#CCC'; }
                                    }
                                },
                                null
                            ],
                            [
                                {
                                    table: {
                                        headerRows: 1,
                                        widths: [50, '*'],
                                        body: [
                                            [{ text: 'Dados do cliente', style: 'sectionHeader', colSpan: 2 }, null],
                                            [{ text: 'Cliente:', bold: true }, { text: order.customer.name }],
                                            [{ text: 'CFP/CNPJ:', bold: true }, { text: order.customer.cpfCnpj.length > 11 ? formatCNPJ(order.customer.cpfCnpj) : formatCPF(order.customer.cpfCnpj) }],
                                            [{ text: 'Endereço:', bold: true }, { text: order.customer.address }],
                                        ]
                                    },
                                    colSpan: 2,
                                    layout: {
                                        hLineWidth: function (i: any, node: { table: { body: string | any[]; }; }) {
                                            return (i === node.table.body.length) ? 1 : 0;
                                        },
                                        vLineWidth: function () { return 0; },
                                        hLineColor: function () { return '#CCC'; }
                                    }
                                },
                                null
                            ],
                        ]
                    },
                    layout: 'noBorders',
                    style: 'defaultStyle'
                },

                { text: 'Dados do pedido', style: 'sectionHeader' },
                {
                    columns: [
                        { width: '25%', text: `Número cliente: ${order.order.customerNumber}`, alignment: 'left' },
                        { width: '50%', text: `Previsão faturamento: ${order.order.billingForecast}`, alignment: 'center' },
                        { width: '25%', text: `Frete: ${formatBRL(order.order.freightValue)}`, alignment: 'right' }
                    ]
                },
                { text: `Valor total: ${formatBRL(order.order.totalValue)}`, bold: true, margin: [0, 5, 0, 15], alignment: 'left'},

                { text: 'Itens do pedido', style: 'sectionHeader' },
                {
                    table: {
                        headerRows: 1,
                        widths: ['10%', 'auto', '15%', '15%', '15%', '15%', '15%'],
                        body: [
                            [
                                { text: 'Produto', style: 'tableHeader' },
                                { text: 'Descrição', style: 'tableHeader' },
                                { text: 'Qtd. solicitada', style: 'tableHeader', alignment: 'right' },
                                { text: 'Qtd. atendida', style: 'tableHeader', alignment: 'right' },
                                { text: 'Valor unit.', style: 'tableHeader', alignment: 'right' },
                                { text: 'Desconto', style: 'tableHeader', alignment: 'right' },
                                { text: 'Total item', style: 'tableHeader', alignment: 'right' }
                            ],
                            ...order.items.map((item: any) => [
                                { text: item.code, alignment: 'right' },
                                item.description,
                                { text: item.requestedQuantity, alignment: 'right' },
                                { text: item.settedQuantity, alignment: 'right' },
                                { text: formatBRL(item.unitValue), alignment: 'right' },
                                { text: formatBRL(item.discountValue), alignment: 'right' },
                                { text: formatBRL(item.totalValue), alignment: 'right' }
                            ])
                        ]
                    },
                    layout: {
                        hLineWidth: function (i: any, node: { table: { body: string | any[]; }; }) {
                            return (i > 1) ? 1 : 0;
                        },
                        vLineWidth: function () { return 0; },
                        hLineColor: function () { return '#F2F2F2'; },
                        fillColor: function (i: any, node: { table: { body: string | any[]; }; }) {
                            return (i % 2 === 0) ? '#F2F2F2' : null;
                        }
                    },
                    style: 'defaultStyle'
                }, 
            ],
            footer: function (currentPage: number, pageCount: number) {
                return {
                    columns: [
                        { width: '50%', text: `Pedido: ${order.order.number}`, margin: [10, 10, 0, 0], fontSize: 8, color: '#888' },
                        { width: '50%', text: `Página ${currentPage} de ${pageCount}`, alignment: 'right', margin: [0, 10, 10, 0], fontSize: 8, color: '#888' },
                    ]
                };
            },
            styles: {
                defaultStyle: {
                    fontSize: 10,
                },
                header: {
                    fontSize: 18,
                    bold: true,
                    alignment: 'center',
                    margin: [0, 0, 0, 10]
                },
                sectionHeader: {
                    fontSize: 14,
                    bold: true,
                    color: '#4a90e2',
                    margin: [0, 2, 0, 2]
                },
                tableHeader: {
                    bold: true,
                    fontSize: 10,
                    color: 'white',
                    fillColor: '#4a90e2',
                    alignment: 'center'
                }
            }
        };

        pdfMake.createPdf(docDefinition).download(`Pedido_${order.order.number}.pdf`);
    }
}
